import { InputCommerceAttribute } from 'utils/api/models/numbers/usage';

import {
	extractAttributeValue,
	extractAttributeUnitOfMeasure,
	extractAttributeValuesAll,
	parsers,
	extractPrice,
	getExtractedUnit,
	getExtractedValue,
	extractPlanDetails,
	extractPayGDetails,
	CustomAttribute,
} from 'utils/ecommerce/services/extract';

export interface SinglePackage {
	_entry?: types.ecommerce.CatalogEntryView[] | InputCommerceAttribute[];
	name: string;
	shortDescription: string;
	longDescription: string;
	buyable: boolean;
	uniqueID: string;
	commerceUniqueIdToActivate?: string;
	parentCatalogGroupID?: string[];
	partNumber: string;
	partNumberRaw: string;
	selfDeactivate: boolean;
	selfActivate: boolean;
	roamingType: string;
	duration: number;
	description: string;
	autorenewal: boolean;
	dataReadable: string;
	dataValue: number;
	dataUnit: string;
	nightDataReadable: string;
	nightDataValue: number;
	nightDataUnit: string;
	voiceReadable: string;
	voiceValue: number;
	voiceUnit: string;
	nightDataValidity: string;
	price: number;
	priceOffer: number;
	postpaidPlanPrice: number;
	priceCurrency: string;
	promoProductId: string;
	promoVoucherIds: string[];
	hayyakPlus: boolean;
	multiactivate: boolean;
	excludedBy: string[];
	requiredProducts: string[];
	place: string;
	planType: string;
	planRelated: string;
	fullImage: string;
	thumbnail: string;
	planDetails: any[];
	payGDetails: any[];
	doubleDataIncluded: string;
	whatsappPackageSize: string;
	extendedBy: string;
	extendedByLabel: string;
	destination: string;
	iconMain: string;
	iconAdd: string;
	contractId: string;
	baqatPlus: boolean;
	attributes: [];
	toolbarShortDescription: string;
	includedData: string;
	includedMinutes: string;
	alUfuq: boolean;
	unlCallNumbersCount: string;
	isHybrid: boolean;
	convergentEligible: boolean;
	hayyakUpsell: boolean;
	hierarchyIndex: number;
	groupID: string;
	promoVoucherValue: string;
	findOutMoreLink?: string;
	productType: string;
	installationFee: string;
	monthlyFee: string;
	ebuConfigProduct: string;
	vatValue: string;
}

const model: types.models.InputModel = {
	_entry: 'entry',
	_root_: (model: SinglePackage) => {
		const _entryAsArray = Array.isArray(model._entry) ? model._entry : [model._entry];
		const packages = (_entryAsArray as any).map((singlePackage: SinglePackage) => {
			const dataReadable = extractAttributeValue(singlePackage, CustomAttribute.LOC_DATA_PACKAGE_SIZE);
			const nightDataReadable = extractAttributeValue(singlePackage, CustomAttribute.LOC_EXTRA_DATA_PACKAGE_SIZE);
			const voiceReadable = extractAttributeValue(singlePackage, CustomAttribute.LOC_MIN_PACKAGE_SIZE);
			return {
				alUfuq: extractAttributeValue(singlePackage, CustomAttribute.AL_UFUQ),
				all: singlePackage,
				autorenewal: extractAttributeValue(singlePackage, CustomAttribute.RENEWAL) === 'AUTO',
				backgroundUrl: extractAttributeValue(singlePackage, CustomAttribute.BACKGROUND_URL),
				baqatPlus: extractAttributeValue(singlePackage, CustomAttribute.BAQAT_PLUS) === 'TRUE' || false,
				buyable: parsers.boolean(singlePackage.buyable),
				commerceUniqueIdToActivate: extractAttributeValue(singlePackage, CustomAttribute.ACT_THIS_ON_RENEW),
				contractId: extractAttributeValue(singlePackage, CustomAttribute.CONTRACT_TO_ACTIVATE),
				convergentEligible: parsers.boolean(
					extractAttributeValue(singlePackage, CustomAttribute.CONVERGENT_ELIGIBLE)
				),
				dataReadable,
				dataUnit: getExtractedUnit(dataReadable),
				dataValue: getExtractedValue(dataReadable),
				definitionSource: extractAttributeValue(singlePackage, CustomAttribute.DEFINITION_SOURCE),
				description: extractAttributeValue(singlePackage, CustomAttribute.PRODUCT_DESCRIPTION),
				destination: extractAttributeValue(singlePackage, CustomAttribute.DESTINATION),
				doubleDataIncluded:
					singlePackage.doubleDataIncluded ||
					extractAttributeValue(singlePackage, CustomAttribute.DOUBLE_DATA_INCLUDED),
				doubleDataUnit: extractAttributeUnitOfMeasure(singlePackage, CustomAttribute.DOUBLE_DATA_INCLUDED),
				duration:
					parsers.duration(
						extractAttributeValue(singlePackage, [
							CustomAttribute.DURATION_CATEGORY,
							CustomAttribute.DURATION,
							CustomAttribute.PACKAGE_VALIDITY,
						])
					) || extractAttributeValue(singlePackage, [CustomAttribute.CONTRACT_DURATION]),
				excludedBy: extractAttributeValuesAll(singlePackage, CustomAttribute.EXC_PROD).filter(
					(e) => !!e && e.replace(/[A-z]/g, '')
				),
				extendedBy:
					singlePackage.extendedBy || extractAttributeValue(singlePackage, CustomAttribute.IS_EXTENDED_BY),
				extendedByLabel:
					singlePackage.extendedByLabel ||
					extractAttributeValue(singlePackage, CustomAttribute.IS_EXTENDED_BY_LABEL),
				findOutMoreLink:
					singlePackage.findOutMoreLink ||
					extractAttributeValue(singlePackage, CustomAttribute.FIND_OUT_MORE_LINK),
				fullImage: singlePackage.fullImage || extractAttributeValue(singlePackage, CustomAttribute.FULL_IMAGE),
				groupID: extractAttributeValue(singlePackage, CustomAttribute.GROUP_ID),
				hayyakPlus: parsers.boolean(extractAttributeValue(singlePackage, CustomAttribute.HAYYAK_PLUS)),
				hayyakUpsell:
					singlePackage.hayyakUpsell || extractAttributeValue(singlePackage, CustomAttribute.HAYYAK_UPSELL),
				hierarchyIndex: parseInt(
					extractAttributeValue(singlePackage, CustomAttribute.HIERARCHY_INDEX) || 0,
					10
				),
				iconAdd: extractAttributeValue(singlePackage, CustomAttribute.ICON_ADD),
				iconMain: extractAttributeValue(singlePackage, CustomAttribute.ICON_MAIN),
				includedData: extractAttributeValue(singlePackage, CustomAttribute.INCLUDED_DATA),
				includedMinutes: extractAttributeValue(singlePackage, CustomAttribute.INCLUDED_MINUTES),
				isHybrid: parsers.boolean(extractAttributeValue(singlePackage, CustomAttribute.HYBRID_OFFER)),
				longDescription:
					singlePackage.longDescription ||
					extractAttributeValue(singlePackage, CustomAttribute.LONG_DESCRIPTION),
				multiactivate: parsers.boolean(
					extractAttributeValue(singlePackage, CustomAttribute.MULTI_ACTIVATE, false)
				),
				name: singlePackage.name,
				nightDataReadable,
				nightDataUnit: getExtractedUnit(nightDataReadable),
				nightDataValidity: extractAttributeValue(singlePackage, CustomAttribute.LOC_EXTRA_PACKAGE_LABEL),
				nightDataValue: getExtractedValue(nightDataReadable),
				parents: (singlePackage.parentCatalogGroupID ?? []).map((parent: string) => {
					if (parent.includes('_')) {
						return parent.split('_')[1];
					} else {
						return parent;
					}
				}),
				partNumber: singlePackage.partNumber,
				partNumberRaw: !!singlePackage.partNumber && singlePackage.partNumber.replace(/[A-z]/g, ''),
				payGDetails: extractPayGDetails(singlePackage),
				place: extractAttributeValue(singlePackage, CustomAttribute.DEFINITION_SOURCE),
				planDetails: extractPlanDetails(singlePackage),
				planRelated: extractAttributeValue(singlePackage, CustomAttribute.PLAN_RELATED),
				planType: extractAttributeValue(singlePackage, CustomAttribute.PLAN_TYPE),
				priceOffer: extractPrice(singlePackage as any, 'Offer').price,
				price:
					extractAttributeValue(singlePackage, CustomAttribute.SUBSCRIPTION_RATE) ||
					extractPrice(singlePackage as any).price,
				priceCurrency: extractPrice(singlePackage as any).currency,
				promoProductId: extractAttributeValue(singlePackage, CustomAttribute.PROMO_PRODUCT_ID),
				promoVoucherIds: extractAttributeValuesAll(singlePackage, CustomAttribute.PROMO_VOUCHER_ID),
				promoVoucherValue: extractAttributeValuesAll(singlePackage, CustomAttribute.PROMO_VOUCHER_VALUE),
				requiredProducts: extractAttributeValuesAll(singlePackage, CustomAttribute.REQ_PROD).filter(
					(e) => !!e && e.replace(/[A-z]/g, '')
				),
				roamingType: extractAttributeValue(singlePackage, CustomAttribute.ROAMING_TYPE),
				selfActivate: parsers.boolean(
					extractAttributeValue(singlePackage, CustomAttribute.SELF_ACTIVATE, true)
				),
				selfDeactivate: parsers.boolean(
					extractAttributeValue(singlePackage, CustomAttribute.SELF_DEACTIVATE, false)
				),
				shortDescription:
					singlePackage.shortDescription ||
					extractAttributeValue(singlePackage, CustomAttribute.SHORT_DESCRIPTION),
				thumbnail: singlePackage.thumbnail || extractAttributeValue(singlePackage, CustomAttribute.THUMBNAIL),
				toolbarShortDescription:
					singlePackage.toolbarShortDescription ||
					extractAttributeValue(singlePackage, CustomAttribute.TOOLBAR_SHORT_DESC),
				uniqueID: singlePackage.uniqueID,
				unlCallNumbersCount: extractAttributeValue(singlePackage, CustomAttribute.UNL_NUM_COUNT),
				vasTag: extractAttributeValue(singlePackage, CustomAttribute.TAG),
				voiceReadable,
				voiceUnit: getExtractedUnit(voiceReadable),
				voiceValue: getExtractedValue(voiceReadable),
				whatsappPackageSize:
					singlePackage.whatsappPackageSize ||
					extractAttributeValue(singlePackage, CustomAttribute.WHATSAPP_PACKAGE_SIZE),
				whatsappPackageUnit: extractAttributeUnitOfMeasure(
					singlePackage,
					CustomAttribute.WHATSAPP_PACKAGE_SIZE
				),
				productType: extractAttributeValue(singlePackage, CustomAttribute.PRODUCT_TYPE),
				installationFee: extractAttributeValue(singlePackage, CustomAttribute.INSTALLATION_FEE),
				monthlyFee: extractAttributeValue(singlePackage, CustomAttribute.MONTHLY_FEE),
				ebuConfigProduct: extractAttributeValue(singlePackage, CustomAttribute.EBU_CONFIG_PRODUCT),
				vatValue: extractAttributeValue(singlePackage, CustomAttribute.VAT_VALUE),
			};
		});
		return packages;
	},
};

export default model;
